import React, { FC, useCallback, useEffect, useState, useMemo } from "react"
import { observer } from "mobx-react-lite"
import { Appearance, ViewStyle, ImageStyle, Platform } from "react-native"
import { Button, Screen, AutoImage, Text, useAutoFeederPond, QRCodeScanner } from "../../../../components"
import { color, nativeBaseTheme } from "../../../../theme"
import { CompositeScreenProps } from "@react-navigation/native"
import { View, Modal, useDisclose, Toast } from "native-base"
import _ from "lodash"
import RNBluetoothClassic from "react-native-bluetooth-classic"
import { translate } from "../../../../i18n"
import { SafeAreaView } from "react-native-safe-area-context"
import {
  requestAccessFineLocationPermission,
  requestBluetoothConnectPermission,
  requestBluetoothScanPermission
} from "../../../../utils/permissions";
import { useStores } from "../../../../models"
import { AutoFeederPond } from "../../../../database"
import { delay } from "../../../../utils/delay"
import { goBack, navigate } from "../../../../navigators"
import JalaAnalytic from "../../../../utils/jala-analytic"

const unrecognized = require("./unrecognized_qr.png")

const ROOT: ViewStyle = {
  flexGrow: 1,
  backgroundColor: color.palette.black
}

const MODAL: ViewStyle = {
  margin: 0,
  width: '100%',
  justifyContent: 'flex-end',
  position: 'absolute',
  bottom: 0,
  flex: 1,
  backgroundColor: color.palette.white,
  paddingHorizontal: 16,
  paddingBottom: 20,
  alignItems: 'center'
}


const BUTTON_DETAIL_ITEM: ViewStyle = {
  alignSelf: "center",
  paddingVertical: 8,
  borderRadius: 5,
  width: '100%',
  marginBottom: 10,
  marginTop: 32
}

const UNRECOGNIZED_IMAGE: ImageStyle = {
  width: '100%',
  height: 215,
  resizeMode: 'contain',
}

export const AutoFeederCreateQRScanScreen: FC<{ colorScheme?: "light" | "dark" } & CompositeScreenProps<any, any>> = observer(function AutoFeederCreateQRScanScreen(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props
  const { isOpen, onClose, onOpen } = useDisclose()
  const [ bluetoothEnabled, setBluetoothEnabled ] = useState<boolean>(false)
  const [ bluetoothGranted, setBluetoothGranted ] = useState<boolean>(false)
  const { setAutoFeederPond } = useAutoFeederPond()

  const {
    appStore: {
      farmStore,
      farmStore: { pondStoreModels }
    },
  } = useStores()

  const autoFeederSections = useMemo(() => {
    const pondsData = pondStoreModels.map((pondStore) => {
      return ({
        title: pondStore,
        data: pondStore.autoFeederPonds.length || !farmStore.loadingAutoFeederPond ? pondStore.autoFeederPonds : [],
      })
    });
    return pondsData.filter((pond) => pond.data.length !== 0)
  }, [pondStoreModels, farmStore.loadingAutoFeederPond])

  const getDevices = useCallback(async () => {
    let granted = await requestBluetoothConnectPermission()
      .then(() => requestBluetoothScanPermission())
      .catch((e) => {
        Toast.show({ title: e.message });
        return false;
      });
    if (!granted) return
    granted = await requestAccessFineLocationPermission().catch((e) => {
      Toast.show({ title: e.message });
      return false;
    });
    if (!granted) return
    setBluetoothGranted(true);
  }, [])

  useEffect(() => {
    if (Platform.OS === 'web') return undefined
    const bluetoothEnabled = RNBluetoothClassic.onBluetoothEnabled((event) => setBluetoothEnabled(event.enabled))
    const bluetoothDisabled = RNBluetoothClassic.onBluetoothDisabled((event) => setBluetoothEnabled(event.enabled))
    RNBluetoothClassic.isBluetoothEnabled().catch(() => false).then((enabled) => {
      getDevices().catch()
      setBluetoothEnabled(enabled)
    })
    return () => {
      bluetoothEnabled.remove()
      bluetoothDisabled.remove()
    }
  }, [getDevices])

  useEffect(() => {
    if (bluetoothEnabled && bluetoothGranted) {
      if (!isOpen) RNBluetoothClassic.cancelDiscovery().catch()
    }
  }, [bluetoothEnabled, bluetoothGranted, getDevices, isOpen])

  const onItemExist = useCallback(async (item) => {
    farmStore.set('pondId', item.title.pondId)
    farmStore.pondStore.set('autoFeederPondId', item.data[0].id)
    setAutoFeederPond(AutoFeederPond.hydrate(farmStore.pondStore.autoFeederPond))
    await delay(1)
    navigate('Home.AutoFeederPond.Stack', 'Home.AutoFeederPond.Detail.Stack', { id: farmStore.pondStore.autoFeederPondId })
  }, [farmStore, setAutoFeederPond])

  const onReadQr = useCallback(async ({ data }) => {
    const isExisted = autoFeederSections.find((asto) => asto.data.some((item) => item.auto_feeder_address === data))
    if (isExisted) onItemExist(isExisted)
    // else if (params?.fromList && !isExisted) onOpen()
    else {
      const connected = await RNBluetoothClassic.isDeviceConnected(data).catch(() => false)
      const device = connected ?
        await RNBluetoothClassic.getConnectedDevice(data).catch(() => null) :
        await RNBluetoothClassic.connectToDevice(data).catch(() => null)
      if (!device) onOpen()
      else {
        Toast.show({ title: device.id })
        JalaAnalytic.log('Mobile App : Add Asto');
        navigate('Home.AutoFeederPond.Stack', 'Home.AutoFeederPond.Create', { device: { id: device.id, name: device.name } })
      }
    }
  }, [autoFeederSections, onItemExist, onOpen])

  return (
    <Screen
      style={ROOT}
      preset="scroll"
      colorScheme={colorScheme}
      backgroundBar={colorScheme === 'light' ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
    >
      <QRCodeScanner
        onRead={onReadQr}
        showMarker
        cameraProps={{ ratio: '1:1', autoFocus: "on" }}
        bottomContent={
          <View justifyContent="center" alignItems="center">
            <Text tx="autofeederCreateQRScanScreen.description" preset="body2" color={color.palette.white} colorScheme={colorScheme} textAlign="center" />
          </View>
        }
        fadeIn={false}
      />
      <Modal isOpen={isOpen} onClose={onClose}>
        <SafeAreaView />
        <View
          style={MODAL}
          borderTopRadius="10px"
          backgroundColor={colorScheme === 'light' ? nativeBaseTheme.colors.light["50"] : nativeBaseTheme.colors.dark["50"]}
        >
          <Text
            text={translate("autofeederCreateQRScanScreen.unrecognizedModalTitle")}
            transformText={_.upperFirst}
            preset="title1"
            colorScheme={colorScheme}
            marginTop="16px" marginX="7px"
            color={color.primaryDarker}
          />
          <AutoImage source={unrecognized} style={UNRECOGNIZED_IMAGE} />
          <Text
            text={translate("autofeederCreateQRScanScreen.unrecognizedModalSubtitle")}
            transformText={_.upperFirst}
            preset="body2"
            colorScheme={colorScheme}
            marginBottom="8px"
            textAlign="center"
          />
          <Button
            testID="jala:id/cancel-bluetooth-modal"
            text={translate("common.back")}
            preset="primary"
            textPreset="title3"
            colorScheme={colorScheme}
            style={BUTTON_DETAIL_ITEM}
            onPress={() => {
              onClose()
              goBack('Home.Stack')
            }}
          />
        </View>
      </Modal>
    </Screen>
  )
})
